import React from 'react'
import { IconButton, Box, Typography } from '@material-ui/core'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import { TitleMediumBold, TitleH2BoldLarge, TitleH2Bold } from '../../theme/typography'
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
  DotGroup
} from 'pure-react-carousel'
import 'pure-react-carousel/dist/react-carousel.es.css'
import PropTypes from 'proptypes'
import ErrorBoundary from '../../errorBoundary'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  root: {
    color: (props) => (props.textWhite ? theme.colors.white : theme.colors.blue5),
    backgroundColor: (props) => (props.bgcolor ? theme.colors.cream : 'transparent'),
    margin: theme.spacing(5, 0)
  },
  carousel: {
    position: 'relative',
    maxWidth: 730,
    margin: 'auto',
    padding: theme.spacing(0, 6),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 12)
    }
  },
  minHeight: {
    minHeight: 140
  },
  title: {
    color: theme.colors.white,
    padding: theme.spacing(3, 0, 6),
    [theme.breakpoints.up('md')]: {
      fontSize: '6rem'
    }
  },
  titleBlue: {
    position: 'relative',
    fontSize: '2.5rem',
    padding: theme.spacing(3, 0, 5),
    color: (props) => (props.textWhite ? theme.colors.white : theme.colors.blue1)
  },
  buttons: {
    appearance: 'none',
    color: (props) => (props.textWhite ? theme.colors.white : theme.colors.blue5),
    background: 'transparent',
    border: 'none',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)'
  },
  left: {
    left: 0
  },
  right: {
    right: 0
  },
  icon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) scale(0.6)',
    transition: 'transform .2s ease'
  },
  textContent: {
    '& a': {
      color: (props) => (props.textWhite ? theme.colors.white : theme.colors.blue1),
      '&:hover': {
        color: (props) => (props.textWhite ? theme.colors.white : theme.colors.blue1)
      }
    }
  },
  blueDots: {
    marginTop: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    '& button': {
      width: 34,
      height: 34,
      padding: 8,
      margin: 5,
      border: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      borderRadius: '50%',
      outline: 'none',
      '& span': {
        width: 4,
        height: 4,
        backgroundColor: (props) => (props.textWhite ? theme.colors.white : theme.colors.grey5),
        borderRadius: '50%',
        transform: 'scale(1)',
        transition: 'transform .4s'
      },
      '&.carousel__dot--selected span': {
        backgroundColor: (props) => (props.textWhite ? theme.colors.white : theme.colors.blue1),
        transform: 'scale(3)'
      }
    }
  }
}))

function CustomerCarousel(props) {
  const classes = useStyles(props)

  return (
    <ErrorBoundary>
      <div className={classes.root}>
        {props.largeTitle ? (
          <TitleH2BoldLarge className={classes.title} variant="h2" align="center">
            {props.title}
          </TitleH2BoldLarge>
        ) : (
          <TitleH2Bold className={classes.titleBlue} variant="h2" align="center">
            {props.title}
          </TitleH2Bold>
        )}

        <CarouselProvider
          naturalSlideWidth={574}
          naturalSlideHeight={400}
          totalSlides={props.list.length}
          isIntrinsicHeight={true}
          infinite
          isPlaying={props.autoplay}
        >
          <div className={classes.carousel}>
            <Slider className={classes.minHeight}>
              {props.list.map((slide, i) => {
                return (
                  <Slide index={i} key={`banner-${i}`}>
                    <Typography
                      variant="h5"
                      component="p"
                      color={props.textWhite ? 'currentcolor' : 'textSecondary'}
                      gutterBottom
                    >
                      {new Date(slide.date).toLocaleDateString(props.locale, {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </Typography>
                    <TitleMediumBold component="h3" gutterBottom>
                      {slide.title}
                    </TitleMediumBold>
                    <Typography
                      className={classes.textContent}
                      variant="body1"
                      component="div"
                      color={props.textWhite ? 'currentcolor' : 'textSecondary'}
                      dangerouslySetInnerHTML={{ __html: slide.content }}
                    />
                  </Slide>
                )
              })}
            </Slider>
            {props.list.length > 1 && (
              <Box>
                <ButtonBack className={`${classes.buttons} ${classes.left}`}>
                  <IconButton color={'inherit'} component="span">
                    <ArrowBackIosIcon fontSize="inherit" />
                  </IconButton>
                </ButtonBack>
                <ButtonNext className={`${classes.buttons} ${classes.right}`}>
                  <IconButton color={'inherit'} component="span">
                    <ArrowForwardIosIcon fontSize="inherit" />
                  </IconButton>
                </ButtonNext>
              </Box>
            )}
          </div>
          <DotGroup className={classes.blueDots} />
        </CarouselProvider>
      </div>
    </ErrorBoundary>
  )
}

export default CustomerCarousel

CustomerCarousel.propTypes = {
  bgcolor: PropTypes.bool,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      date: PropTypes.string,
      content: PropTypes.string
    })
  )
}

CustomerCarousel.defaultProps = {
  list: []
}
